import type { ReactNode } from 'react';
import styled from 'styled-components';
import type { PtoVolumePosition } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { decisionAllowed, isClosedRow, type RdEditRights } from '../rdEdits/logic';
import { decisionLabel } from './format';
import { Badge } from './styles';

/** Две кнопки в строке («Принять Откл.» / «Спорно»): графа не сжимается до кнопки в
 *  строке, когда таблице тесно (ширина экрана ПТО — до 1280 px). */
const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

/** «Статус»: позиция на проверке (П5) и решение инженера (HITL), если оно есть. */
export function StatusCell({ position: p }: { position: PtoVolumePosition }) {
  return (
    <>
      {p.needs_review ? <Badge $tone="warn">на проверку</Badge> : <Badge $tone="muted">ок</Badge>}
      {p.decision && (
        <>
          {' '}
          <Badge $tone={p.decision === 'rejected' ? 'warn' : 'ok'}>
            {decisionLabel(p.decision)}
          </Badge>
        </>
      )}
    </>
  );
}

interface DecisionCellProps {
  position: PtoVolumePosition;
  canWrite: boolean;
  /** Права правок РД (№8): при включённом флаге «Откл.» и его снятие на строке с
   *  количеством, решения по строке с правкой, ручной строке и позиции по
   *  документу — только «Инженер+»; «Спорно» — с листом и причиной. */
  rights: RdEditRights;
  onAnnotate: (id: string, decision: string) => void;
  /** «Спорно» с листом и причиной (правки №8 включены). */
  onDispute: () => void;
  /** Кнопки правки «Инженер+» («Исправить…»). */
  extra?: ReactNode;
}

/** «Решение»: разметка позиции инженером — принять, отклонить, спорно. Правки
 *  РД выключены — как прежде; включены — по правилам №8 (недоступное решение —
 *  неактивная кнопка с подсказкой), «Спорно» открывает окно с листом и
 *  причиной. Закрытая позиция (отменена, отозвана, закрыта ревизией) решений не
 *  принимает. */
export function DecisionCell({
  position: p,
  canWrite,
  rights,
  onAnnotate,
  onDispute,
  extra,
}: DecisionCellProps) {
  if (isClosedRow(p)) {
    return <Badge $tone="muted">закрыто</Badge>;
  }
  const button = (decision: 'accepted' | 'rejected' | 'disputed', label: string) => {
    const check = decisionAllowed(p, decision, rights, canWrite);
    const dispute = decision === 'disputed' && rights.enabled;
    return (
      <Button
        $variant="ghost"
        $size="sm"
        disabled={!check.allowed}
        title={
          check.reason ?? (dispute ? '«Спорно» с листом и причиной — в журнал правок' : undefined)
        }
        onClick={() => (dispute ? onDispute() : onAnnotate(p.id, decision))}
      >
        {label}
      </Button>
    );
  };
  return (
    <Actions>
      {button('accepted', 'Принять')}
      {button('rejected', 'Откл.')}
      {button('disputed', rights.enabled ? 'Спорно…' : 'Спорно')}
      {extra}
    </Actions>
  );
}
